/**
 * StatementMatchPanel
 * -------------------
 * A supplier mails a statement; this says in one line whether their number and
 * ours agree, and names the invoices each side is missing.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Scale, CheckCircle2, AlertTriangle, Upload, Paperclip,
  RefreshCw, Trash2, Plus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { API } from "@/App";
import { openAuthedDoc } from "@/lib/openAuthedDoc";

const money = (v) => `$${(Number(v) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const StatementMatchPanel = ({ token, supplierId, supplierName, canWrite, onChanged }) => {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [file, setFile] = useState(null);
  const fileInput = useRef(null);
  const [form, setForm] = useState({
    supplier_name: supplierName || "",
    statement_date: new Date().toISOString().slice(0, 10),
    period_label: "",
    statement_total: "",
    statement_invoice_numbers: "",
    notes: "",
  });

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/payables/statements`, {
        params: { supplier_id: supplierId || undefined },
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows(r.data?.statements || []);
      setSummary(r.data?.summary || {});
    } catch {
      setRows([]);
    } finally { setLoading(false); }
  }, [token, supplierId]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const body = () => ({
    supplier_id: supplierId || null,
    supplier_name: form.supplier_name.trim(),
    statement_date: form.statement_date || null,
    period_label: form.period_label || null,
    statement_total: Number(form.statement_total) || 0,
    statement_invoice_numbers: form.statement_invoice_numbers || null,
    notes: form.notes || null,
  });

  const check = async () => {
    if (!form.supplier_name.trim()) return toast.error("Name the supplier");
    setChecking(true);
    try {
      const r = await axios.post(`${API}/payables/statements/preview`, body(), { headers });
      setPreview(r.data?.reconciliation);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not check that statement");
    } finally { setChecking(false); }
  };

  const save = async () => {
    if (!form.supplier_name.trim()) return toast.error("Name the supplier");
    setSaving(true);
    try {
      const r = await axios.post(`${API}/payables/statements`, body(), { headers });
      const id = r.data?.statement?.id;
      if (file && id) {
        const fd = new FormData();
        fd.append("file", file);
        await axios.post(`${API}/payables/statements/${id}/files`, fd, { headers });
      }
      const rec = r.data?.statement?.reconciliation;
      toast.success(rec?.matched
        ? "Statement matches your ledger"
        : `Statement is off by ${money(Math.abs(rec?.variance || 0))} — flagged`);
      setOpen(false);
      setPreview(null);
      setFile(null);
      setForm(f => ({ ...f, statement_total: "", statement_invoice_numbers: "", notes: "" }));
      load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save that statement");
    } finally { setSaving(false); }
  };

  const recheck = async (id) => {
    try {
      const r = await axios.post(`${API}/payables/statements/${id}/recheck`, {}, { headers });
      const rec = r.data?.reconciliation;
      toast.success(rec?.matched ? "Now matches your ledger" : `Still off by ${money(Math.abs(rec?.variance || 0))}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not re-check"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this statement record?")) return;
    try {
      await axios.delete(`${API}/payables/statements/${id}`, { headers });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
  };

  const Verdict = ({ rec }) => (
    <div className={`rounded-md border px-3 py-2.5 ${rec.matched
      ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50"}`}
      data-testid="statement-verdict">
      <p className={`text-sm font-black inline-flex items-center gap-1.5 ${rec.matched ? "text-emerald-800" : "text-red-800"}`}>
        {rec.matched ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
        {rec.matched
          ? "Their statement agrees with your ledger"
          : `Off by ${money(Math.abs(rec.variance))} — they say ${money(rec.statement_total)}, you have ${money(rec.recorded_open_balance)} open`}
      </p>
      <p className="text-[11px] text-slate-600 mt-1">
        {rec.invoice_count} invoice{rec.invoice_count === 1 ? "" : "s"} on file
        {rec.statement_total > rec.recorded_open_balance
          ? " · they are billing for more than you have recorded"
          : rec.statement_total < rec.recorded_open_balance
            ? " · you have more open than they are billing"
            : ""}
      </p>
      {rec.missing_from_our_ledger?.length > 0 && (
        <p className="text-[11px] text-red-800 mt-1.5" data-testid="statement-missing">
          <strong>On their statement, not in your ledger:</strong> {rec.missing_from_our_ledger.join(", ")}
        </p>
      )}
      {rec.already_paid_but_still_on_statement?.length > 0 && (
        <p className="text-[11px] text-amber-900 mt-1.5" data-testid="statement-already-paid">
          <strong>You already paid these, they are still billing:</strong>{" "}
          {rec.already_paid_but_still_on_statement.map(i => i.invoice_number).join(", ")}
        </p>
      )}
      {rec.open_but_not_on_statement?.length > 0 && (
        <p className="text-[11px] text-slate-700 mt-1.5" data-testid="statement-extra">
          <strong>Open on your side, not on their statement:</strong>{" "}
          {rec.open_but_not_on_statement.map(i => `${i.invoice_number} (${money(i.balance)})`).join(", ")}
        </p>
      )}
    </div>
  );

  return (
    <div className="rounded-lg border border-slate-200 overflow-hidden" data-testid="statement-match-panel">
      <div className="bg-indigo-700 text-white px-3 py-2 text-[11px] font-black uppercase tracking-wider flex items-center gap-2">
        <Scale className="w-3.5 h-3.5" /> Statement match
        {summary.with_variance > 0 && (
          <span className="bg-red-500 px-2 py-0.5 rounded-full text-[10px]" data-testid="statement-variance-count">
            {summary.with_variance} off by {money(Math.abs(summary.total_variance || 0))}
          </span>
        )}
        <span className="flex-1" />
        {canWrite && (
          <button onClick={() => setOpen(true)}
            className="bg-white/15 hover:bg-white/25 px-2 py-0.5 rounded text-[10px] inline-flex items-center gap-1"
            data-testid="statement-new">
            <Plus className="w-3 h-3" /> Check a statement
          </button>
        )}
      </div>

      {loading ? (
        <div className="py-8 text-center text-slate-400 text-sm">
          <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading statements…
        </div>
      ) : rows.length === 0 ? (
        <p className="px-3 py-8 text-center text-sm text-slate-400">
          No statements checked yet — enter a supplier's statement total and this will tell you
          whether it agrees with what you have recorded.
        </p>
      ) : (
        <div className="divide-y divide-slate-100">
          {rows.map(st => {
            const rec = st.reconciliation || {};
            return (
              <div key={st.id} className="px-3 py-2.5" data-testid={`statement-${st.id}`}>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="font-bold text-sm text-slate-900">{st.supplier_name}</span>
                  <span className="text-xs text-slate-500">{st.statement_date || st.period_label || ""}</span>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase ${st.status === "matched"
                    ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
                    {st.status === "matched" ? "matches" : `off by ${money(Math.abs(rec.variance || 0))}`}
                  </span>
                  <span className="flex-1" />
                  <span className="text-xs text-slate-500">theirs {money(st.statement_total)}</span>
                  <span className="text-xs text-slate-500">ours {money(rec.recorded_open_balance)}</span>
                </div>
                {st.status !== "matched" && (
                  <div className="mt-1.5"><Verdict rec={rec} /></div>
                )}
                <div className="flex flex-wrap items-center gap-2 mt-1.5">
                  {(st.attachments || []).map(a => (
                    <button key={a.id} type="button"
                      onClick={() => openAuthedDoc(a.url.replace(/^\/api/, ""), { token, filename: a.name })}
                      className="text-[11px] text-cyan-700 underline inline-flex items-center gap-1"
                      data-testid={`statement-file-${a.id}`}>
                      <Paperclip className="w-3 h-3" />{a.name}
                    </button>
                  ))}
                  {canWrite && (
                    <>
                      <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[10px] font-bold text-slate-600"
                        onClick={() => recheck(st.id)} data-testid={`statement-recheck-${st.id}`}>
                        <RefreshCw className="w-3 h-3 mr-1" /> Re-check
                      </Button>
                      <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[10px] font-bold text-red-600"
                        onClick={() => remove(st.id)} data-testid={`statement-delete-${st.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-[70] bg-black/50 flex items-start justify-center overflow-y-auto p-4"
          onClick={() => setOpen(false)}>
          <div className="bg-white rounded-xl w-full max-w-xl my-8 shadow-2xl" onClick={(e) => e.stopPropagation()}
            data-testid="statement-dialog">
            <div className="px-5 py-4 border-b border-slate-200">
              <h3 className="text-lg font-black text-slate-900 inline-flex items-center gap-2">
                <Scale className="w-5 h-5 text-indigo-600" /> Check a supplier statement
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Enter their closing balance; we compare it against what you have recorded and name
                the invoices either side is missing.
              </p>
            </div>
            <div className="p-5 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-black uppercase tracking-wider text-slate-500 mb-1">Supplier</label>
                  <Input value={form.supplier_name} onChange={(e) => set("supplier_name", e.target.value)}
                    disabled={!!supplierId} className="h-9 text-sm" data-testid="statement-supplier" />
                </div>
                <div>
                  <label className="block text-[10px] font-black uppercase tracking-wider text-slate-500 mb-1">Statement total they claim</label>
                  <Input type="number" step="0.01" value={form.statement_total}
                    onChange={(e) => set("statement_total", e.target.value)}
                    className="h-9 text-sm" data-testid="statement-total" />
                </div>
                <div>
                  <label className="block text-[10px] font-black uppercase tracking-wider text-slate-500 mb-1">Statement date</label>
                  <Input type="date" value={form.statement_date} onChange={(e) => set("statement_date", e.target.value)}
                    className="h-9 text-sm" data-testid="statement-date" />
                </div>
                <div>
                  <label className="block text-[10px] font-black uppercase tracking-wider text-slate-500 mb-1">Period label</label>
                  <Input value={form.period_label} onChange={(e) => set("period_label", e.target.value)}
                    placeholder="October 2026" className="h-9 text-sm" data-testid="statement-period" />
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-black uppercase tracking-wider text-slate-500 mb-1">
                  Invoice numbers on their statement
                </label>
                <Textarea value={form.statement_invoice_numbers}
                  onChange={(e) => set("statement_invoice_numbers", e.target.value)} rows={3}
                  placeholder="Paste or type them, any separator: 10045, 10046 10047"
                  className="text-sm font-mono" data-testid="statement-numbers" />
                <p className="text-[10px] text-slate-400 mt-0.5">
                  Optional — but this is what lets us name the invoices each side is missing.
                </p>
              </div>
              <div className="rounded-md border border-cyan-200 bg-cyan-50/60 px-3 py-2.5">
                <input type="file" ref={fileInput} className="hidden"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  accept=".pdf,.png,.jpg,.jpeg,.webp,.csv,.xlsx"
                  data-testid="statement-file-input" />
                <Button type="button" size="sm" variant="outline" onClick={() => fileInput.current?.click()}
                  className="h-7 text-[11px] gap-1.5 bg-white" data-testid="statement-attach">
                  <Upload className="w-3 h-3" /> Attach their statement
                </Button>
                {file && (
                  <p className="text-[11px] text-cyan-900 mt-1.5 inline-flex items-center gap-1">
                    <Paperclip className="w-3 h-3" /> {file.name}
                  </p>
                )}
              </div>
              {preview && <Verdict rec={preview} />}
            </div>
            <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setOpen(false)} data-testid="statement-cancel">
                Cancel
              </Button>
              <Button variant="outline" size="sm" onClick={check} disabled={checking}
                className="gap-1.5" data-testid="statement-check">
                {checking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Scale className="w-3.5 h-3.5" />}
                Compare
              </Button>
              <Button size="sm" onClick={save} disabled={saving}
                className="bg-indigo-600 hover:bg-indigo-700 gap-1.5" data-testid="statement-save">
                {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Save result
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StatementMatchPanel;
